import { useEffect, useState } from 'react';
import { css } from '@emotion/react';
import { PaymentAndInsuranceKnowledge } from '@weave/schema-gen-ts/dist/schemas/ai-settings/v1/ai_settings.pb';
import { useTranslation } from '@frontend/i18n';
import { Button, Text } from '@frontend/design-system';
import { AddCustomDetails } from '../../common/add-custom-details';
import { KnowledgeFormSection } from '../../common/knowledge-form-section';
import { AcceptedPaymentTypesInput } from './accepted-payment-types-input';
import { InsuranceInput } from './insurance-input';
import { usePaymentKnowledgeForm } from './use-payment-knowledge-form';

type Props = {
  locationId: string;
  isManageAll?: boolean;
  presentFields?: Record<keyof PaymentAndInsuranceKnowledge, boolean>;
};

export const PaymentKnowledgeForm = ({ locationId, isManageAll = false, presentFields }: Props) => {
  const { t } = useTranslation('ai-receptionist-settings');

  const [selectedOptionalFields, setSelectedOptionalFields] = useState<(keyof PaymentAndInsuranceKnowledge)[]>([]);

  const {
    getFieldProps,
    settingsData,
    fieldErrors,
    isPaymentTypesDirty,
    isInsuranceAcceptedInNetworkDirty,
    isInsuranceAcceptedOutOfNetworkDirty,
    isInsuranceNotAcceptedDirty,
    hasPaymentTypes,
    hasInsuranceAcceptedInNetwork,
    hasInsuranceAcceptedOutOfNetwork,
    hasInsuranceNotAccepted,
    handleDeleteField: storeHandleDeleteField,
    handleAddField: storeHandleAddField,
    updateDraft,
    setFieldError,
  } = usePaymentKnowledgeForm({ locationId, isManageAll });

  const handleDeleteField = (field: keyof PaymentAndInsuranceKnowledge) => {
    setSelectedOptionalFields((prev) => prev.filter((f) => f !== field));
    storeHandleDeleteField(field);
  };

  const handleAddField = <T extends keyof PaymentAndInsuranceKnowledge>(
    field: T,
    value: PaymentAndInsuranceKnowledge[T]
  ) => {
    setSelectedOptionalFields((prev) => [...prev, field]);
    storeHandleAddField(field, value);
  };

  useEffect(() => {
    const fieldsToAdd: (keyof PaymentAndInsuranceKnowledge)[] = [];
    if (hasPaymentTypes) fieldsToAdd.push('paymentTypes');
    if (hasInsuranceAcceptedInNetwork) fieldsToAdd.push('insuranceAcceptedInNetwork');
    if (hasInsuranceAcceptedOutOfNetwork) fieldsToAdd.push('insuranceAcceptedOutOfNetwork');
    if (hasInsuranceNotAccepted) fieldsToAdd.push('insuranceNotAccepted');

    setSelectedOptionalFields((prev) => {
      const newFields = fieldsToAdd.filter((f) => !prev.includes(f));
      return newFields.length > 0 ? [...prev, ...newFields] : prev;
    });
  }, [hasPaymentTypes, hasInsuranceAcceptedInNetwork, hasInsuranceAcceptedOutOfNetwork, hasInsuranceNotAccepted]);

  return (
    <div css={styles.container}>
      {selectedOptionalFields.map((field) => {
        switch (field) {
          case 'paymentTypes':
            return (
              <KnowledgeFormSection
                key='paymentTypes'
                title={t('Accepted Payment Types')}
                description={t('What forms of payment do you accept?')}
                isDirty={isPaymentTypesDirty}
                hasError={!!fieldErrors[locationId]?.paymentTypes}
                onDelete={() => handleDeleteField('paymentTypes')}
                showBulkOverrideWarningAlert={isManageAll && !!presentFields?.paymentTypes}
              >
                <AcceptedPaymentTypesInput fieldProps={getFieldProps('paymentTypes')} />
              </KnowledgeFormSection>
            );
          case 'insuranceAcceptedInNetwork':
            return (
              <KnowledgeFormSection
                key='insuranceAcceptedInNetwork'
                title={t('Insurance Accepted (In-Network)')}
                description={t('Which insurance do you accept in-network?')}
                isDirty={isInsuranceAcceptedInNetworkDirty}
                hasError={!!fieldErrors[locationId]?.insuranceAcceptedInNetwork}
                onDelete={() => handleDeleteField('insuranceAcceptedInNetwork')}
                showBulkOverrideWarningAlert={isManageAll && !!presentFields?.insuranceAcceptedInNetwork}
              >
                <InsuranceInput
                  value={settingsData.insuranceAcceptedInNetwork}
                  onChange={(val) => updateDraft(locationId, { insuranceAcceptedInNetwork: val })}
                  onErrorChange={(hasError) => setFieldError(locationId, 'insuranceAcceptedInNetwork', hasError)}
                  isRequired={!isManageAll}
                />
              </KnowledgeFormSection>
            );
          case 'insuranceAcceptedOutOfNetwork':
            return (
              <KnowledgeFormSection
                key='insuranceAcceptedOutOfNetwork'
                title={t('Insurance Accepted (Out-of-Network)')}
                description={t('Which insurance do you accept out-of-network?')}
                isDirty={isInsuranceAcceptedOutOfNetworkDirty}
                hasError={!!fieldErrors[locationId]?.insuranceAcceptedOutOfNetwork}
                onDelete={() => handleDeleteField('insuranceAcceptedOutOfNetwork')}
                showBulkOverrideWarningAlert={isManageAll && !!presentFields?.insuranceAcceptedOutOfNetwork}
              >
                <InsuranceInput
                  value={settingsData.insuranceAcceptedOutOfNetwork}
                  onChange={(val) => updateDraft(locationId, { insuranceAcceptedOutOfNetwork: val })}
                  onErrorChange={(hasError) => setFieldError(locationId, 'insuranceAcceptedOutOfNetwork', hasError)}
                  isRequired={!isManageAll}
                />
              </KnowledgeFormSection>
            );
          case 'insuranceNotAccepted':
            return (
              <KnowledgeFormSection
                key='insuranceNotAccepted'
                title={t('Insurance Not Accepted')}
                description={t('Which insurance do you not accept?')}
                isDirty={isInsuranceNotAcceptedDirty}
                hasError={!!fieldErrors[locationId]?.insuranceNotAccepted}
                onDelete={() => handleDeleteField('insuranceNotAccepted')}
                showBulkOverrideWarningAlert={isManageAll && !!presentFields?.insuranceNotAccepted}
              >
                <InsuranceInput
                  value={settingsData.insuranceNotAccepted}
                  onChange={(val) => updateDraft(locationId, { insuranceNotAccepted: val })}
                  onErrorChange={(hasError) => setFieldError(locationId, 'insuranceNotAccepted', hasError)}
                  isRequired={!isManageAll}
                />
              </KnowledgeFormSection>
            );
          default:
            return null;
        }
      })}

      {/* Footer Add Options */}
      {(!hasPaymentTypes ||
        !hasInsuranceAcceptedInNetwork ||
        !hasInsuranceAcceptedOutOfNetwork ||
        !hasInsuranceNotAccepted) && (
        <div css={styles.addFieldsContainer}>
          <div css={styles.chipsSection}>
            <Text color='subdued' size='medium'>
              {t('Click to add more details to this page')}
            </Text>
            <div
              css={{
                display: 'flex',
                gap: 'var(--fabric-space-gap-sm)',
                marginTop: 'var(--fabric-space-gap-xs)',
                flexWrap: 'wrap',
              }}
            >
              {!hasPaymentTypes && (
                <Button
                  variant='secondary'
                  size='small'
                  iconName='plus-small'
                  onClick={() => handleAddField('paymentTypes', '')}
                >
                  {t('Accepted Payment Types')}
                </Button>
              )}
              {!hasInsuranceAcceptedInNetwork && (
                <Button
                  variant='secondary'
                  size='small'
                  iconName='plus-small'
                  onClick={() => handleAddField('insuranceAcceptedInNetwork', [])}
                >
                  {t('Insurance Accepted (In-Network)')}
                </Button>
              )}
              {!hasInsuranceAcceptedOutOfNetwork && (
                <Button
                  variant='secondary'
                  size='small'
                  iconName='plus-small'
                  onClick={() => handleAddField('insuranceAcceptedOutOfNetwork', [])}
                >
                  {t('Insurance Accepted (Out-of-Network)')}
                </Button>
              )}
              {!hasInsuranceNotAccepted && (
                <Button
                  variant='secondary'
                  size='small'
                  iconName='plus-small'
                  onClick={() => handleAddField('insuranceNotAccepted', [])}
                >
                  {t('Insurance Not Accepted')}
                </Button>
              )}
            </div>
          </div>
        </div>
      )}
      {!isManageAll && <AddCustomDetails />}
    </div>
  );
};

const styles = {
  container: css`
    display: flex;
    flex-direction: column;
    gap: var(--fabric-space-gap-xl);
  `,
  addFieldsContainer: css`
    display: flex;
    flex-direction: column;
  `,
  chipsSection: css`
    display: flex;
    flex-direction: column;
  `,
};
